/**
 * ONE FILE AT A TIME, STRAIGHT TO R2.
 *
 * 🚨 WHY THIS EXISTS. The checklist used to post every photo and video as a SINGLE
 * multipart request to our server, with a 75s timeout, retried three times. Phone video
 * makes that unwinnable on a weak signal: the whole upload times out, the server receives
 * nothing, and the driver is told to "add the required photos" they are looking at on
 * their screen. One job on 2026-09-04 was retried seven times; another reported 0 of 6
 * received. Drivers have complained about this repeatedly.
 *
 * A big upload is not one job, it is six. Each file gets its own presigned PUT, its own
 * timeout and its own retry, so a dropped connection costs ONE photo and one retry rather
 * than the entire checklist. The seller inspection form has worked this way for a while;
 * this is the driver app catching up.
 */
import { Platform } from 'react-native';
import { api } from './api';

export interface UploadedMedia { url: string; kind: 'photo' | 'video' }

/** Per-file budget. Generous for one file on a bad connection, and it cannot strand the
 *  whole submission the way one 75s window over six files could. */
const PER_FILE_TIMEOUT_MS = 120_000;
const ATTEMPTS = 3;

function guess(uri: string, kind: 'photo' | 'video'): { ext: string; contentType: string } {
  const lower = uri.split('?')[0].toLowerCase();
  if (kind === 'video') {
    if (lower.endsWith('.mp4')) return { ext: 'mp4', contentType: 'video/mp4' };
    if (lower.endsWith('.webm')) return { ext: 'webm', contentType: 'video/webm' };
    return { ext: 'mov', contentType: 'video/quicktime' };
  }
  if (lower.endsWith('.png')) return { ext: 'png', contentType: 'image/png' };
  if (lower.endsWith('.heic') || lower.endsWith('.heif')) return { ext: 'heic', contentType: 'image/heic' };
  return { ext: 'jpg', contentType: 'image/jpeg' };
}

/** Read the local uri into something fetch can PUT. */
async function toBody(uri: string): Promise<{ body: Blob | ArrayBuffer; contentType?: string }> {
  const res = await fetch(uri);
  const blob = await res.blob();
  // A blob carries its REAL type. Trust it over the extension when it has one: a PWA
  // reload can hand back a video/mp4 blob from a uri that still looks like .mov.
  return { body: blob, contentType: blob.type || undefined };
}

/** What the sign endpoint accepts. Checked here so a photo it will refuse fails with a
 *  reason, instead of three identical round trips that end in "did not upload". */
const SIGNABLE = /^(image\/(jpeg|png|webp|heic|heif|gif|avif)|video\/(mp4|quicktime|webm|x-m4v|3gpp))$/i;

export interface UploadResult { url: string | null; reason?: string }

/**
 * Upload one file and return its public URL, or null if it could not be uploaded.
 *
 * Never throws: one unreadable photo must not take the other five down with it.
 */
export async function uploadOne(
  uri: string,
  kind: 'photo' | 'video',
  onProgress?: (msg: string) => void,
  folderOverride?: string,
): Promise<string | null> {
  return (await uploadOneDetailed(uri, kind, onProgress, folderOverride)).url;
}

/**
 * The same upload, plus WHY it failed.
 *
 * 2026-10-13, Norman, pickup on job cmusxd1de11yw10ljaxgbglyq: six taps of Complete, and
 * every time the same one of his four photos never arrived. The app only counted failures,
 * so all he was ever told was "add the required photos", with all four on his screen. The
 * reason now travels with the failure so the screen can name the photo and the server can
 * log what went wrong.
 */
export async function uploadOneDetailed(
  uri: string,
  kind: 'photo' | 'video',
  onProgress?: (msg: string) => void,
  folderOverride?: string,
): Promise<UploadResult> {
  // Sign-up documents live in their own folder; everything else is checklist media.
  const folder = folderOverride ?? (kind === 'video' ? 'checklists/video' : 'checklists');
  let reason = 'unknown';
  for (let attempt = 0; attempt < ATTEMPTS; attempt++) {
    try {
      const g = guess(uri, kind);
      let contentType = g.contentType;
      let read: { body: Blob | ArrayBuffer; contentType?: string };
      try {
        read = await toBody(uri);
      } catch (e: any) {
        // The file is gone (the phone cleared it, or a reload killed a blob: link). Retrying
        // cannot bring it back; only a retake can.
        return { url: null, reason: `unreadable file (${e?.message || e})` };
      }
      const body = read.body;
      if (read.contentType) contentType = read.contentType;
      const size = (body as Blob).size ?? (body as ArrayBuffer).byteLength ?? -1;
      if (size === 0) return { url: null, reason: 'empty file' };
      if (!SIGNABLE.test(contentType)) return { url: null, reason: `file type ${contentType || 'unknown'} not accepted` };

      const signed = await api.post(
        '/uploads/sign',
        { folder, contentType, fileName: `checklist-${kind}-${Date.now()}-${attempt}.${g.ext}` },
        { timeout: 20_000 },
      );
      const { uploadUrl, publicUrl } = (signed.data ?? {}) as { uploadUrl?: string; publicUrl?: string };
      if (!uploadUrl || !publicUrl) return { url: null, reason: 'signing unavailable' }; // R2 not configured: caller falls back to multipart

      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), PER_FILE_TIMEOUT_MS);
      try {
        const put = await fetch(uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': contentType },
          body: body as BodyInit,
          signal: ctrl.signal,
        });
        if (!put.ok) throw new Error(`PUT ${put.status}`);
      } finally {
        clearTimeout(timer);
      }

      // .mov/HEIC become browser-safe mp4/jpg here, exactly as the seller form does.
      // Best effort: a failed transcode returns the original url rather than losing it.
      try {
        const fin = await api.post('/uploads/finalize', { url: publicUrl }, { timeout: 60_000 });
        const finalUrl = (fin.data ?? {}).url as string | undefined;
        return { url: finalUrl || publicUrl };
      } catch {
        return { url: publicUrl };
      }
    } catch (e: any) {
      const status = e?.response?.status;
      reason = status ? `sign ${status} ${e?.response?.data?.error || ''}`.trim() : (e?.name === 'AbortError' ? 'timed out' : String(e?.message || e));
      onProgress?.(`Retrying ${kind}...`);
      if (attempt === ATTEMPTS - 1) {
        console.warn(`[mediaUpload] ${kind} failed after ${ATTEMPTS} attempts:`, reason);
        return { url: null, reason };
      }
      await new Promise((r) => setTimeout(r, 1200 * (attempt + 1)));
    }
  }
  return { url: null, reason };
}

/**
 * Upload everything, in order, reporting progress.
 *
 * Returns what made it, the url each local file became, and every file that did not make
 * it with the reason, so the caller can name the exact photo to retake instead of telling
 * the driver to "add photos" they are looking at.
 */
export async function uploadAll(
  photoUris: string[],
  videoUris: string[],
  onProgress?: (done: number, total: number) => void,
  already: Record<string, string> = {},
): Promise<{
  photoUrls: string[]; videoUrls: string[]; failed: number; signingUnavailable: boolean;
  urlByUri: Record<string, string>; failures: Array<{ uri: string; kind: 'photo' | 'video'; reason: string }>;
}> {
  const total = photoUris.length + videoUris.length;
  const photoUrls: string[] = [];
  const videoUrls: string[] = [];
  const urlByUri: Record<string, string> = {};
  const failures: Array<{ uri: string; kind: 'photo' | 'video'; reason: string }> = [];
  let done = 0;

  const one = async (uri: string, kind: 'photo' | 'video') => {
    // A file that went up on an earlier tap of Complete is not sent again.
    const r: UploadResult = already[uri] ? { url: already[uri] } : await uploadOneDetailed(uri, kind);
    if (r.url) { urlByUri[uri] = r.url; (kind === 'photo' ? photoUrls : videoUrls).push(r.url); }
    else failures.push({ uri, kind, reason: r.reason || 'unknown' });
    onProgress?.(++done, total);
  };
  for (const uri of photoUris) await one(uri, 'photo');
  for (const uri of videoUris) await one(uri, 'video');
  // Only call it "signing unavailable" if literally nothing got through - otherwise this
  // was ordinary per-file failure and the caller should NOT re-send everything as one blob.
  const signingUnavailable = total > 0 && photoUrls.length === 0 && videoUrls.length === 0;
  return { photoUrls, videoUrls, failed: failures.length, signingUnavailable, urlByUri, failures };
}

/**
 * Sign-up documents, one PUT each.
 *
 * 🚨 REGISTRATION POSTED FOUR DOCUMENTS AS ONE MULTIPART BODY - profile photo, licence,
 * car insurance and general liability - and on LTE that body ends early. busboy reports
 * "Unexpected end of form" and the applicant is told their photos did not finish
 * uploading, so a driver cannot create an account at all. Reported 2026-09-06 from a
 * phone on LTE.
 *
 * Same treatment as the checklist: each document gets its own presigned PUT, its own
 * timeout and its own retry, so a dropped connection costs ONE document rather than the
 * whole sign-up. Returns whatever uploaded plus the names of anything that did not, so
 * the caller can name the missing document instead of failing the lot.
 */
export async function uploadDriverDocs(
  docs: Array<{ field: string; uri: string | null }>,
  onProgress?: (done: number, total: number) => void,
): Promise<{ urls: Record<string, string>; failed: string[]; signingUnavailable: boolean }> {
  const present = docs.filter((d) => !!d.uri) as Array<{ field: string; uri: string }>;
  const urls: Record<string, string> = {};
  const failed: string[] = [];
  let done = 0;

  for (const d of present) {
    const url = await uploadOne(d.uri, 'photo', undefined, 'driver-docs');
    if (url) urls[d.field] = url;
    else failed.push(d.field);
    onProgress?.(++done, present.length);
  }
  // Nothing at all got through: R2 signing is likely unavailable, so the caller should
  // fall back to the old multipart post rather than telling a driver their photos failed.
  const signingUnavailable = present.length > 0 && Object.keys(urls).length === 0;
  return { urls, failed, signingUnavailable };
}
